/**
 * Austrian VAT arithmetic.
 *
 * Rates (§ 10 UStG): 20 % Normalsteuersatz, 13 % and 10 % ermäßigte Steuersätze, 4,9 % for
 * selected basic foods, 0 % for exempt turnover. 19 % exists only in Jungholz and Mittelberg and
 * is accepted but never offered.
 */

export type VatLine = { rate: number; net: number; vat: number };

export const AUSTRIAN_VAT_RATES = [20, 13, 10, 4.9, 0] as const;
/** Rates we accept on import but do not offer in the UI. */
export const ACCEPTED_VAT_RATES = [20, 19, 13, 10, 4.9, 0];

/**
 * Rates that only exist from a date on. Changed by law, not a fix: 4,9 % for milk, butter, eggs,
 * fresh fruit and vegetables, bread, flour, rice, simple pasta and salt since 1 July 2026
 * (§ 10 Abs 1a UStG, BGBl. I Nr. 37/2026) - those goods were taxed at 10 % before.
 */
const RATE_VALID_FROM: Readonly<Record<string, string>> = { "4.9": "2026-07-01" };

/** Whether a rate could be charged for a supply on this date; without a date, any rate can. */
export function isRateValidOn(rate: number, date: string | undefined): boolean {
  const from = RATE_VALID_FROM[String(rate)];
  return !from || !date || date.slice(0, 10) >= from;
}

export const TAX_TREATMENTS = [
  "standard",
  "reverse_charge",
  "eu_intra_community",
  "export",
  "small_business",
  "no_vat",
] as const;
export type TaxTreatment = (typeof TAX_TREATMENTS)[number];

/** Treatments where no Austrian VAT is shown on the document. */
export const VAT_FREE_TREATMENTS: TaxTreatment[] = [
  "reverse_charge",
  "eu_intra_community",
  "export",
  "small_business",
  "no_vat",
];

export function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function vatFromNet(net: number, rate: number): number {
  return round2((net * rate) / 100);
}

export function netFromGross(gross: number, rate: number): number {
  return round2(gross / (1 + rate / 100));
}

export function splitGross(gross: number, rate: number): VatLine {
  const net = netFromGross(gross, rate);
  return { rate, net, vat: round2(gross - net) };
}

export function totalsFromLines(lines: VatLine[]): {
  netTotal: number;
  vatTotal: number;
  grossTotal: number;
} {
  const netTotal = round2(lines.reduce((sum, line) => sum + (line.net || 0), 0));
  const vatTotal = round2(lines.reduce((sum, line) => sum + (line.vat || 0), 0));
  return { netTotal, vatTotal, grossTotal: round2(netTotal + vatTotal) };
}

/** Turns whatever is stored in the `vatLines` JSON column into usable lines. */
export function parseVatLines(value: unknown): VatLine[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      const line = entry as Partial<VatLine> | null;
      const rate = Number(line?.rate);
      const net = Number(line?.net);
      const vat = Number(line?.vat);
      if (!Number.isFinite(rate)) return null;
      return {
        rate,
        net: Number.isFinite(net) ? round2(net) : 0,
        vat: Number.isFinite(vat) ? round2(vat) : vatFromNet(Number.isFinite(net) ? net : 0, rate),
      };
    })
    .filter((line): line is VatLine => line !== null);
}

/** True when the net, VAT and gross of a document are consistent to the cent. */
export function totalsAgree(
  totals: { netTotal?: number; vatTotal?: number; grossTotal?: number },
  tolerance = 0.02,
): boolean {
  const { netTotal, vatTotal, grossTotal } = totals;
  if (netTotal === undefined || vatTotal === undefined || grossTotal === undefined) return true;
  return Math.abs(netTotal + vatTotal - grossTotal) <= tolerance;
}

export function isAcceptedRate(rate: number): boolean {
  return ACCEPTED_VAT_RATES.includes(rate);
}

/**
 * The VAT lines a document is edited with. A document stored with a total but no breakdown - an
 * extraction that found only the amount - gets the line that total implies when the rate is
 * certain: from its net and VAT, or from a treatment that charges none (reverse charge and an
 * i.g. acquisition at the 20 % we owe ourselves, the rest at 0 %). Otherwise none: guessing a rate
 * would put a wrong figure into the UVA.
 */
export function linesForDocument(document: {
  kind?: string;
  taxTreatment?: string;
  vatLines?: unknown;
  netTotal?: number;
  vatTotal?: number;
  grossTotal?: number;
}): VatLine[] {
  const stored = parseVatLines(document.vatLines);
  const gross = document.grossTotal ?? 0;
  if (stored.length > 0 || gross <= 0) return stored;

  const { netTotal: net, vatTotal: vat } = document;
  if (net !== undefined && vat !== undefined && net > 0 && vat > 0) {
    const rate = ACCEPTED_VAT_RATES.find((known) => Math.abs((vat / net) * 100 - known) <= 0.6);
    if (rate !== undefined && totalsAgree({ netTotal: net, vatTotal: vat, grossTotal: gross })) {
      return [{ rate, net, vat }];
    }
    return [];
  }
  const treatment = document.taxTreatment as TaxTreatment | undefined;
  if (!treatment || !VAT_FREE_TREATMENTS.includes(treatment)) return [];
  const selfAssessed =
    document.kind !== "AR" &&
    (treatment === "reverse_charge" || treatment === "eu_intra_community");
  return [{ rate: selfAssessed ? 20 : 0, net: gross, vat: 0 }];
}
